/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */

import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';

import { ShelterShowPropTypes } from 'dpl/shared/utils/customPropTypes';
import { CHARITY_NAVIGATOR_GENERAL_URL } from 'dpl/shared/constants/urls';
import { capitalize, isMobileUA } from 'dpl/shared/utils';
import { get, isEmpty } from 'dpl/shared/utils/object';
import { displayHoursToTimestamp, getDayName } from 'dpl/shared/utils/date';
import SmartImage from 'dpl/components/SmartImage';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import Icon from 'dpl/common/components/Icon';

const isMobile = isMobileUA();

export function DetailsHours({ isExpanded, onClick, operatingHours }) {
  const now = new Date();
  const nowTimestamp = now.getTime();
  const dayName = getDayName(now);
  const { opens: opensToday = '', closes: closesToday = '' } = get(
    operatingHours,
    dayName,
    {}
  );
  const hasOperatingHours = !isEmpty(operatingHours);
  const openTime = displayHoursToTimestamp(opensToday);
  const closeTime = displayHoursToTimestamp(closesToday);
  const isOpenNow = nowTimestamp >= openTime && nowTimestamp < closeTime;

  if (!hasOperatingHours) {
    return null;
  }

  return (
    <div>
      <button className="flex" onClick={onClick} type="button">
        {isOpenNow ? (
          <p className="green">Open now</p>
        ) : (
          <p className="red">Closed now</p>
        )}
        <Icon name={`fat-arrow-${isExpanded ? 'up' : 'down'}`} />
      </button>
      {isExpanded && (
        <ul>
          {Object.entries(operatingHours).map(([day, { opens, closes }]) => (
            <li className="mb1" key={day}>
              {day}: {opens} - {closes}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

DetailsHours.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  operatingHours: PropTypes.object.isRequired
};

export function DetailsDogItem({ dog, onClick }) {
  const label = [dog.age_description, dog.gender, dog.size_description]
    .filter(Boolean)
    .map(capitalize)
    .join(', ');

  return (
    <div className="mh2 mb4 mb2-l">
      <a
        onClick={e => {
          e.preventDefault();
          onClick(dog.id);
        }}
        role="button"
      >
        {dog.photographs.length > 0 && (
          <div className="mb1">
            <SmartImage
              Tag="div"
              alt={dog.photographs[0].alt_text}
              className="w-100 br1 bg-top bg-center-l bg-center-m cover mw-100"
              height={isMobile ? '174px' : '240px'}
              width={isMobile ? '200px' : '300px'}
              crop
              loadingClass="bg-light-gray"
              src={dog.photographs[0].url}
            />
          </div>
        )}
        <p className="primary fw-medium">{dog.name}</p>
        <p className="f2">{label}</p>
      </a>
    </div>
  );
}

DetailsDogItem.propTypes = {
  dog: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    age_description: PropTypes.string,
    gender: PropTypes.string,
    size_description: PropTypes.string,
    photographs: PropTypes.arrayOf(
      PropTypes.shape({
        alt_text: PropTypes.string,
        url: PropTypes.string
      })
    ).isRequired
  }).isRequired,
  onClick: PropTypes.func.isRequired
};

export default class Details extends Component {
  static propTypes = {
    onDogClick: PropTypes.func.isRequired,
    onShelterClose: PropTypes.func.isRequired,
    shelterData: PropTypes.shape({
      meta: PropTypes.shape({
        isSuccess: PropTypes.bool
      }),
      value: PropTypes.shape({
        data: PropTypes.shape(ShelterShowPropTypes)
      }).isRequired
    }).isRequired
  };

  state = { isHoursExpanded: false };

  ref = createRef();

  handleHoursToggle = () =>
    this.setState({ isHoursExpanded: !this.state.isHoursExpanded });

  handleOutsideClick = e => {
    if (!this.ref.current.contains(e.target)) {
      this.props.onShelterClose();
    }
  };

  componentDidMount() {
    if (CONFIG.isTest) {
      return;
    }

    window.appContent.addEventListener('click', this.handleOutsideClick);
  }

  componentWillUnmount() {
    if (CONFIG.isTest) {
      return;
    }

    window.appContent.removeEventListener('click', this.handleOutsideClick);
  }

  render() {
    const { onDogClick, shelterData } = this.props;

    const { isHoursExpanded } = this.state;

    const {
      value: {
        data: {
          charity_navigator_rating: charityNavigatorRating,
          charity_navigator_url: charityNavigatorUrl,
          dogs,
          full_address: address,
          name,
          operating_hours: operatingHours,
          phone_number: phoneNumber,
          cover_photo_url: coverPhotoUrl,
          rating,
          rating_url: ratingUrl,
          website_url: websiteUrl,
          display_website_url: displayWebsiteUrl
        }
      }
    } = shelterData;

    const numDogs = dogs.length;

    return (
      <article className="ShelterDetails overflow-x-hidden" ref={this.ref}>
        <LoadingWrapper isLoading={!shelterData.meta.isSuccess}>
          <div>
            <SmartImage
              Tag="div"
              className="br1 bg-top bg-center-l bg-center-m cover w-100 min-w-100 mw-100"
              loadingClass="bg-light-gray"
              src={coverPhotoUrl}
              height={isMobile ? '200px' : '370px'}
              width={isMobile ? '375px' : '560px'}
              crop
            />
            <div className="pv4 ph6 ph8-lg pv8-lg">
              <h6 className="mb3 mb5-md f4 f6-md">{name}</h6>

              <div className="mb3 mb5-md f2 f3-md">
                {address && (
                  <div className="ShelterDetails__contactItem pv1 flex items-center">
                    <div className="mr4">
                      <Icon name="location-pin" />
                    </div>
                    <span>{address}</span>
                  </div>
                )}
                {phoneNumber && (
                  <div className="ShelterDetails__contactItem pv1 flex items-center">
                    <div className="mr4">
                      <Icon name="phone" />
                    </div>
                    <span>
                      <a href={`tel:${phoneNumber}`} className="link--primary">
                        {phoneNumber}
                      </a>
                    </span>
                  </div>
                )}
                {!isEmpty(operatingHours) && (
                  <div className="ShelterDetails__contactItem pv1 flex items-center">
                    <div className="mr4">
                      <Icon name="clock" />
                    </div>
                    <DetailsHours
                      isExpanded={isHoursExpanded}
                      onClick={this.handleHoursToggle}
                      operatingHours={operatingHours}
                    />
                  </div>
                )}
                {websiteUrl && (
                  <div className="ShelterDetails__contactItem pv1 flex items-center">
                    <div className="mr4">
                      <Icon name="link" />
                    </div>
                    <a
                      href={websiteUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                      className="link--primary"
                    >
                      {displayWebsiteUrl}
                    </a>
                  </div>
                )}
              </div>

              <div className="mb8">
                <p className="fw-medium mb2">Charity navigator score</p>
                <p>
                  <span className="mr1">
                    {charityNavigatorRating || 'Unrated'}
                  </span>
                  <a
                    className="link--primary"
                    href={charityNavigatorUrl || CHARITY_NAVIGATOR_GENERAL_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {charityNavigatorRating ? "What's this?" : 'Why?'}
                  </a>
                </p>
              </div>

              {numDogs > 0 && (
                <div>
                  <p className="mb3 fw-medium f4-md">
                    {numDogs === 1
                      ? `${numDogs} adoptable dog`
                      : `${numDogs} adoptable dogs`}
                  </p>
                  <div className="nh2">
                    <ul className="row no-gutters">
                      {dogs.map((dog, idx) => (
                        <li className="col-6 col-lg-12 col-xl-6" key={idx}>
                          <DetailsDogItem dog={dog} onClick={onDogClick} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {rating > 0.0 && (
                <div className="mb3 flex items-center">
                  <div className="ShelterDetails__rating br-100 bg-blue-500 white flex items-center tc mr2">
                    <span className="db center">{rating}</span>
                  </div>
                  <span>
                    {rating} out of 5 stars on&nbsp;
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      className="link--primary"
                      href={ratingUrl}
                    >
                      Google
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>
        </LoadingWrapper>
      </article>
    );
  }
}
